/**
 * Maps a fetched proposal/contract (+ related customer, property, lead) to the
 * props consumed by <ProposalDocument />. Keeps the two view pages thin.
 */
import type { ProposalDocumentProps } from '../components/ProposalDocumentPdf'
import { buildDefaultDocumentData, getDocumentTemplate } from './proposalDocumentTemplates'
import type { DocumentData } from '../types/agreement'

function pickContactName(customer: any, lead: any): string {
  const c = customer || lead
  if (!c) return ''
  const parts = [c.contact_first_name, c.contact_last_name].filter(Boolean)
  if (parts.length) return parts.join(' ')
  return c.contact_name || ''
}

function pickCompany(customer: any, lead: any, contactName: string): string {
  return (
    customer?.company_name ||
    lead?.company_name ||
    customer?.name ||
    lead?.name ||
    contactName ||
    'Customer'
  )
}

function formatEntityAddress(entity: any): string {
  if (!entity) return ''
  const lines: string[] = []
  const addr = entity.address || entity.company_address || entity.street || ''
  const addr2 = entity.address_line_2 || entity.company_address_line_2 || ''
  const city = entity.city || entity.company_city || ''
  const state = entity.state || entity.company_state || ''
  const zip = entity.zip_code || entity.zip || entity.company_zip || ''

  if (addr.trim()) lines.push(addr.trim())
  if (addr2.trim()) lines.push(addr2.trim())
  const cityState = [city, state].filter(Boolean).join(', ')
  const cityLine = [cityState, zip].filter(Boolean).join(' ')
  if (cityLine.trim()) lines.push(cityLine.trim())
  return lines.join('\n')
}

function pickCustomerAddress(customer: any, lead: any): string {
  if (customer) {
    const formatted = formatEntityAddress(customer)
    if (formatted) return formatted
  }
  if (lead) {
    const formatted = formatEntityAddress(lead)
    if (formatted) return formatted
  }
  return ''
}

function streetFromAddress(value: string): string {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  const commaIndex = trimmed.indexOf(',')
  return commaIndex > 0 ? trimmed.slice(0, commaIndex).trim() : trimmed
}

function pickSiteStreet(
  record: any,
  property: any,
  project: any,
  customer?: any,
  lead?: any
): string {
  const candidates = [
    property?.address,
    project?.address,
    record?.property?.address,
    record?.property_address,
    record?.project_address,
    customer?.address,
    lead?.address,
    lead?.company_address,
  ]

  for (const candidate of candidates) {
    const street = streetFromAddress(String(candidate || ''))
    if (street) return street
  }

  return ''
}

function resolveDocumentData(record: any): DocumentData {
  const saved = record?.document_data
  if (saved && typeof saved === 'object' && Object.keys(saved).length > 0) {
    return saved as DocumentData
  }
  return buildDefaultDocumentData(record?.service_type)
}

interface RelatedData {
  customer?: any
  lead?: any
  property?: any
  project?: any
  lineItems: any[]
  billingMilestones?: Array<{ label: string; percentage: number; amount: number }>
}

export function proposalToDocumentProps(
  proposal: any,
  related: RelatedData
): ProposalDocumentProps {
  const { customer, lead, property, project, lineItems, billingMilestones } = related
  const contactName = pickContactName(customer, lead)
  const template = getDocumentTemplate(proposal?.service_type)
  return {
    phase: 'proposal',
    serviceType: proposal?.service_type || '',
    documentNumber: proposal?.proposal_number || '',
    title: proposal?.title || '',
    documentData: resolveDocumentData(proposal),
    lineItems: lineItems || [],
    scopeOfWork: proposal?.scope_of_work || '',
    salesTaxStatus: proposal?.sales_tax_status || '',
    taxRate: proposal?.tax_rate || 0,
    monthlyAmount: proposal?.monthly_amount || 0,
    record: {
      phase: 'proposal',
      serviceType: proposal?.service_type || '',
      title: proposal?.title || '',
      customerCompany: pickCompany(customer, lead, contactName),
      contactName,
      contactFirstName: customer?.contact_first_name || lead?.contact_first_name || contactName,
      recipientAddress: pickCustomerAddress(customer, lead),
      projectAddress: pickSiteStreet(proposal, property, project, customer, lead),
      subject: template.subject,
      date: proposal?.issue_date || proposal?.created_at || null,
      validUntil: proposal?.valid_until || null,
      startDate: proposal?.start_date || null,
      endDate: proposal?.end_date || null,
      contractType: proposal?.contract_type || '',
      paymentTerms: proposal?.payment_terms ?? '',
      billingFrequency: proposal?.billing_frequency || '',
      progressMilestones: billingMilestones || undefined,
    },
  }
}

export function contractToDocumentProps(
  contract: any,
  related: RelatedData
): ProposalDocumentProps {
  const { customer, lead, property, project, lineItems, billingMilestones } = related
  const contactName = pickContactName(customer, lead)
  const template = getDocumentTemplate(contract?.service_type)
  return {
    phase: 'contract',
    serviceType: contract?.service_type || '',
    documentNumber: contract?.contract_number || '',
    title: contract?.title || '',
    documentData: resolveDocumentData(contract),
    lineItems: lineItems || [],
    scopeOfWork: contract?.scope_of_work || '',
    salesTaxStatus: contract?.sales_tax_status || '',
    taxRate: contract?.tax_rate || 0,
    monthlyAmount: contract?.monthly_amount || 0,
    record: {
      phase: 'contract',
      serviceType: contract?.service_type || '',
      title: contract?.title || '',
      customerCompany: pickCompany(customer, lead, contactName),
      contactName,
      contactFirstName: customer?.contact_first_name || lead?.contact_first_name || contactName,
      recipientAddress: pickCustomerAddress(customer, lead),
      projectAddress: pickSiteStreet(contract, property, project, customer, lead),
      subject: template.subject,
      date: contract?.start_date || contract?.created_at || null,
      validUntil: null,
      startDate: contract?.start_date || null,
      endDate: contract?.end_date || null,
      contractType: contract?.contract_type || '',
      paymentTerms: contract?.payment_terms ?? '',
      billingFrequency: contract?.billing_frequency || '',
      progressMilestones: billingMilestones || undefined,
    },
  }
}
